import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { PageEvent } from '@angular/material';
import { Router } from '@angular/router';
import { SatPopover } from '@ncstate/sat-popover';

import { RentalSalesResult } from '../../shared/models/rental-sales-result.model';
import { State } from '../../shared/state-view/state.model';
import { SearchQuery } from '../models/search-query.model';
import { storageAvailable } from '../../util/helpers';

/**
 * Results list for the Rentals/Sales Finder, mirroring {@link FboFinderResultsListingComponent}. Renders the
 * paged result cards (each linking to the existing `/business/:id` detail page), a filter popover, and relays
 * hover events for map sync.
 */
@Component({
  selector: 'app-rental-sales-finder-results-listing',
  templateUrl: './rental-sales-finder-results-listing.component.html',
  styleUrls: ['./rental-sales-finder-results-listing.component.scss']
})
export class RentalSalesFinderResultsListingComponent {
  @Input() query: SearchQuery;
  @Input() rentalSales: Array<RentalSalesResult>;
  @Input() state: State = State.Empty;
  @Input() activeRentalSales: RentalSalesResult;
  @Input() errorMessage: string;
  @Input() pageIndex = 0;
  @Input() pageSize = 25;
  @Input() totalRentalSales = 0;
  @Output() rentalSalesMouseEnter = new EventEmitter<RentalSalesResult>();
  @Output() rentalSalesMouseLeave = new EventEmitter<RentalSalesResult>();
  @Output() filtersChanged = new EventEmitter<SearchQuery>();
  @Output() pageChanged = new EventEmitter<PageEvent>();

  @ViewChild('rentalSalesListEl') rentalSalesListEl: ElementRef;
  @ViewChildren('rentalSalesEl', { read: ElementRef }) rentalSalesEls: QueryList<ElementRef>;
  @ViewChild('rentalSalesFiltersPopover') rentalSalesFiltersPopover: SatPopover;

  constructor(private _router: Router) {}

  /**
   * Records the current search results URL before navigating to a business's detail page, so that page's
   * "Back" button can return to these exact results (see BusinessPageComponent.goBack). Also clears the
   * airport back-target so the business opened from search doesn't fall back to "Back to <airport>".
   */
  onRentalSalesSelected() {
    if (storageAvailable('sessionStorage')) {
      sessionStorage.setItem('businessFinderSearchReturn', this._router.url);
      sessionStorage.removeItem('lastAirport');
    }
  }

  onRentalSalesHover(rentalSales: RentalSalesResult, hover: boolean) {
    if (hover) {
      this.rentalSalesMouseEnter.emit(rentalSales);
    } else {
      this.rentalSalesMouseLeave.emit(rentalSales);
    }
  }

  onFiltersChanged() {
    this.filtersChanged.emit(this.query);
  }

  /**
   * Returns a jQuery element container for the given business.
   */
  getRentalSalesEl(rentalSales: RentalSalesResult) {
    const i = this.rentalSales.indexOf(rentalSales);
    if (i < 0) {
      return null;
    }
    const el = this.rentalSalesEls.toArray()[i];
    // @ts-ignore
    return $(el.nativeElement);
  }

  /**
   * Scrolls the list to the specified business (used for map hover sync).
   */
  public scrollToRentalSales(rentalSales: RentalSalesResult) {
    if (!this.rentalSalesListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.rentalSalesListEl.nativeElement);
    const $el = this.getRentalSalesEl(rentalSales);
    if (!$el) {
      return;
    }
    const position = $el[0].offsetTop;
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: position }, 500);
  }

  onPageChanged(e: PageEvent) {
    this.pageChanged.emit(e);
    if (!this.rentalSalesListEl) {
      return;
    }
    // @ts-ignore
    const $containerEl = $(this.rentalSalesListEl.nativeElement);
    $containerEl.stop(true);
    $containerEl.animate({ scrollTop: 0 }, 500);
  }
}
